import React, { Suspense, useEffect, useRef, useState } from 'react';
import { ArrowRight, BarChart3, Check, Download, Link2, Mail, Menu, Moon, Plus, ShoppingBag, Sparkles, Sun, Upload, X } from 'lucide-react';
import './LandingPage.css'; // shared lp-* styles still used by the marketing sub-pages
import './Home.css';
import { lazyNamed } from '../../lib/lazyNamed';
import { hasPhoto } from '../../lib/media';
import { applySeo, slugFromPath } from '../../lib/seo';

// Sub-pages (pricing, about, legal…) load only when someone opens one.
const MarketingPage = lazyNamed(() => import('./marketing/MarketingPage'), 'MarketingPage');

/* ============================================================================
   LANDING PAGE — marketing site at skrl.net/
   Home is built from the sections below; every other slug renders a
   MarketingPage inside the same nav + footer. CTAs route into the studio
   AuthGate via onStartFree / onSignIn.
   ============================================================================ */

// Hero phones · example products (the front phone cycles through them)
// Demo photos (public/demo) layered over the old gradients · glyphs are the fallback
const SLIDES = [
  { bg: `url(/demo/hoodie-back.jpg) center / cover no-repeat, radial-gradient(ellipse at 30% 20%, #ffb4c6 0%, #c4a1ff 40%, #4f3cd9 100%)`, thumb: '/demo/knit-cream.jpg', glyph: '🧥', text: 'the oversized hoodie drop you asked for', title: 'Pillow Hoodie', price: '$68.00', likes: '12.4K' },
  { bg: `url(/demo/serum-dropper.jpg) center / cover no-repeat, radial-gradient(ellipse at 70% 30%, #ffe8a8 0%, #ffa86b 45%, #ef4370 100%)`, thumb: '/demo/serum-hold.jpg', glyph: '💧', text: 'the serum that saved my 6am flight skin', title: 'Midnight Serum', price: '$42.00', likes: '8.2K' },
  { bg: `url(/demo/laptop-window.jpg) center / cover no-repeat, radial-gradient(ellipse at 50% 70%, #a8ffd4 0%, #5bd4a9 40%, #0f5a4d 100%)`, thumb: '/demo/laptop-stats.jpg', glyph: '📈', text: 'how I grew 500k followers in 11 months', title: 'Grow Your TikTok', price: '$97.00', likes: '21.4K' },
  { bg: `url(/demo/golden-field.jpg) center / cover no-repeat, radial-gradient(ellipse at 70% 70%, #ffc1e3 0%, #a68fff 40%, #2a1b5c 100%)`, thumb: '/demo/film-camera.jpg', glyph: '🎞️', text: 'the exact presets I use on every video', title: 'Cinematic Presets', price: '$29.00', likes: '4.8K' },
];

// Marquee · the kinds of things creators sell
const TILES = [
  ['🧥', 'Hoodies', '#c4a1ff'], ['🎞️', 'LUT packs', '#ffb4c6'], ['📚', 'Courses', '#ffe8a8'], ['💧', 'Skincare', '#a8ffd4'],
  ['🎧', 'Sample packs', '#8fd3f4'], ['🧢', 'Merch', '#ffa86b'], ['📓', 'Templates', '#d4ff47'], ['🕯️', 'Candles', '#fad0c4'],
  ['🏋️', 'Programs', '#84fab0'], ['☕', 'Coffee', '#e0c3a3'], ['🎨', 'Prints', '#a68fff'], ['💍', 'Jewellery', '#ffd6e8'],
];

const STEPS = [
  { icon: Upload, title: 'Upload your videos', body: 'Drop in the same 9:16 clips you already post. No themes to edit, no templates to learn.', tint: '#c4a1ff' },
  { icon: ShoppingBag, title: 'Tag what you sell', body: 'One product per video, physical or digital. Set price, options and stock in a few taps.', tint: '#ffa86b' },
  { icon: Link2, title: 'Share one link', body: 'Put skrl.net/you in your bio. People swipe through your store the way they scroll your feed.', tint: '#5bd4a9' },
];

const NUMBERS = [
  ['0%', 'transaction fees on every plan'],
  ['9:16', 'built for vertical, not bolted on'],
  ['1', 'link in bio for your whole shop'],
  ['14', 'days free, no card needed'],
];

const PLANS = [
  { id: 'starter', name: 'Starter', monthly: 29, yearly: 23, blurb: 'For testing your first products.',
    features: ['1 custom domain', 'Unlimited products', 'Drag-and-drop page designer', '500 AI credits / month'] },
  { id: 'creator', name: 'Creator', monthly: 79, yearly: 63, blurb: 'For creators selling every week.', popular: true,
    features: ['3 custom domains', 'Funnels and A/B tests', '2,400 AI credits / month', 'No skrl branding at checkout', 'Klaviyo and Mailchimp sync'] },
  { id: 'scale', name: 'Scale', monthly: 199, yearly: 159, blurb: 'For teams running a real shop.',
    features: ['Unlimited domains', '10,000 AI credits / month', '5 team seats', 'Custom blocks', 'Priority support'] },
];

const FAQ = [
  ['Do I need to leave Shopify?', 'No. Plenty of creators keep Shopify for their main site and point their social traffic at skrl. Use whichever fits each audience.'],
  ['Do you take a cut of sales?', 'No. Every plan has 0% transaction fees. The only per-sale cost is the payment processor’s standard fee.'],
  ['Who owns the customer list?', 'You do. Every buyer and subscriber is exportable as CSV at any time, including the day you leave.'],
  ['How do AI credits work?', 'Each plan includes a monthly allowance for generating product videos. Different models cost different amounts, and you can see the cost before you generate.'],
  ['Is there a free trial?', 'Yes. Every plan starts with a 14-day trial, and you don’t need a card to sign up.'],
];

const FOOTER_LINKS = [
  ['Product', [['Features', 'features'], ['Pricing', 'pricing'], ['Changelog', 'changelog'], ['Roadmap', 'roadmap']]],
  ['Company', [['About', 'about'], ['Contact', 'contact'], ['Careers', 'careers']]],
  ['Legal', [['Terms', 'terms'], ['Privacy', 'privacy'], ['Security', 'security']]],
];

const Wordmark = ({ className = '' }) => <span className={'lx-mark ' + className}>skrl<span className="lx-mark-dot">.</span></span>;

// Fades sections in as they scroll into view (once). Content stays visible
// without IntersectionObserver, and reduced-motion users skip the animation.
function useReveal(deps) {
  useEffect(() => {
    const els = document.querySelectorAll('.lx-reveal:not(.is-in)');
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function LandingPage({ onStartFree, onSignIn, onViewStore, themeMode, setThemeMode, siteSettings }) {
  const [page, setPage] = useState(() => slugFromPath()); // 'home' | MarketingPage slug
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); applySeo(page); }, [page]);

  // Each sub-page has its own URL (/pricing, /about…) so it can be shared and indexed
  const openPage = (slug) => {
    const path = slug === 'home' ? '/' : `/${slug}`;
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
    setPage(slug);
  };
  useEffect(() => {
    const onPop = () => setPage(slugFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useReveal([page]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Nav links scroll on home, open the full sub-page elsewhere
  const go = (slug) => {
    setMenuOpen(false);
    if (page === 'home' && (slug === 'features' || slug === 'pricing')) {
      document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      openPage(slug);
    }
  };
  const isLight = themeMode === 'light';
  const toggleTheme = () => setThemeMode?.(isLight ? 'dark' : 'light');

  return (
    <div className="lx landing-root" style={{ backgroundColor: 'var(--bg)', color: 'var(--fg)' }}>
      <nav className={'lx-nav' + (scrolled ? ' is-scrolled' : '')}>
        <div className="lx-wrap lx-nav-row">
          <button className="lx-brand" onClick={() => openPage('home')} aria-label="skrl home"><Wordmark /></button>
          <div className="lx-nav-links">
            <button onClick={() => go('features')}>Features</button>
            <button onClick={() => go('pricing')}>Pricing</button>
            <button onClick={onViewStore}>Live demo</button>
          </div>
          <div className="lx-nav-actions">
            {setThemeMode && (
              <button className="lx-icon-btn" onClick={toggleTheme} aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}>
                {isLight ? <Moon size={15} /> : <Sun size={15} />}
              </button>
            )}
            <button className="lx-nav-signin" onClick={onSignIn}>Sign in</button>
            <button className="lx-btn lx-btn-primary lx-btn-sm" onClick={onStartFree}>Start free</button>
            <button className="lx-icon-btn lx-burger" onClick={() => setMenuOpen(true)} aria-label="Open menu"><Menu size={18} /></button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div className="lx-drawer-root">
          <div className="lx-drawer-scrim" onClick={() => setMenuOpen(false)} />
          <div className="lx-drawer">
            <div className="lx-drawer-head">
              <Wordmark />
              <button className="lx-icon-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu"><X size={18} /></button>
            </div>
            <button onClick={() => go('features')}>Features</button>
            <button onClick={() => go('pricing')}>Pricing</button>
            <button onClick={() => { setMenuOpen(false); onViewStore(); }}>Live demo</button>
            <div className="lx-drawer-foot">
              <button className="lx-btn lx-btn-ghost" onClick={() => { setMenuOpen(false); onSignIn(); }}>Sign in</button>
              <button className="lx-btn lx-btn-primary" onClick={() => { setMenuOpen(false); onStartFree(); }}>Start free</button>
            </div>
          </div>
        </div>
      )}

      {page === 'home' ? (
        <main>
          <Hero onStartFree={onStartFree} onViewStore={onViewStore} />
          <Marquee />
          <HowItWorks />
          <Numbers />
          <Features />
          <Pricing onStartFree={onStartFree} />
          <Faq />
          <FinalCta onStartFree={onStartFree} onViewStore={onViewStore} />
        </main>
      ) : (
        <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
          <MarketingPage
            page={page}
            onStartFree={onStartFree}
            onSignIn={onSignIn}
            onViewStore={onViewStore}
            onBack={() => openPage('home')}
            goTo={openPage}
            siteSettings={siteSettings}
          />
        </Suspense>
      )}

      <footer className="lx-foot">
        <div className="lx-wrap lx-foot-grid">
          <div className="lx-foot-lead">
            <Wordmark />
            <p>Vertical-video storefronts for creators.</p>
          </div>
          {FOOTER_LINKS.map(([title, links]) => (
            <div key={title} className="lx-foot-col">
              <h4>{title}</h4>
              {links.map(([label, slug]) => (
                <a key={slug} href={`/${slug}`} onClick={e => { e.preventDefault(); openPage(slug); }}>{label}</a>
              ))}
            </div>
          ))}
        </div>
        <div className="lx-wrap lx-foot-bot">
          <span>© {new Date().getFullYear()} skrl</span>
          <span>/skɜːl/ · scroll without the o</span>
        </div>
      </footer>
    </div>
  );
}

/* ── Sections ─────────────────────────────────────────────────────────────── */

function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <div className={'lx-head lx-reveal' + (center ? ' is-center' : '')}>
      <div className="lx-eyebrow"><span className="lx-eyebrow-dot" />{eyebrow}</div>
      <h2 className="lx-h2">{title}</h2>
      {sub && <p className="lx-lede">{sub}</p>}
    </div>
  );
}

function Hero({ onStartFree, onViewStore }) {
  return (
    <section className="lx-hero">
      <div className="lx-aurora" aria-hidden="true"><span /><span /><span /></div>
      <div className="lx-grid-bg" aria-hidden="true" />
      <div className="lx-wrap lx-hero-grid">
        <div className="lx-hero-copy">
          <div className="lx-pill"><span className="lx-pill-tag">New</span> AI video studio in every plan</div>
          <h1 className="lx-h1">
            Your videos<br />are already<br />the <span className="lx-hl">store<i>.</i></span>
          </h1>
          <p className="lx-hero-sub">
            skrl turns the clips you post into a shop people can swipe through and buy from in a tap.
            Your domain, your customer list, no cut of your sales.
          </p>
          <div className="lx-cta-row">
            <button className="lx-btn lx-btn-primary lx-btn-lg" onClick={onStartFree}>Start free <ArrowRight size={16} /></button>
            <button className="lx-btn lx-btn-ghost lx-btn-lg" onClick={onViewStore}>See a live store</button>
          </div>
          <ul className="lx-hero-notes">
            <li><Check size={14} /> 14-day trial</li>
            <li><Check size={14} /> No card needed</li>
            <li><Check size={14} /> 0% transaction fees</li>
          </ul>
        </div>
        <HeroPhones />
      </div>
    </section>
  );
}

function HeroPhones() {
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  useEffect(() => {
    const t = setInterval(() => { if (!paused.current) setIdx(i => (i + 1) % SLIDES.length); }, 4000);
    return () => clearInterval(t);
  }, []);
  const s = SLIDES[idx];
  const back = (n) => SLIDES[(idx + n) % SLIDES.length];

  return (
    <div className="lx-stage" onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}>
      {/* Two phones fanned out behind the live one */}
      {[['lx-phone-l', back(1)], ['lx-phone-r', back(2)]].map(([cls, sl]) => (
        <div key={cls} className={'lx-phone lx-phone-back theme-dark ' + cls} aria-hidden="true">
          <div className="lx-slide is-on" style={{ background: sl.bg }}>{!hasPhoto(sl.bg) && <div className="lx-slide-glyph">{sl.glyph}</div>}</div>
          <div className="lx-phone-shade" />
        </div>
      ))}

      <div className="lx-phone lx-phone-front theme-dark">
        <div className="lx-phone-bars">
          {SLIDES.map((_, i) => <span key={i} className={i < idx ? 'is-done' : i === idx ? 'is-on' : ''} />)}
        </div>
        <div className="lx-phone-top"><span className="lx-phone-av">✨</span>@novasays</div>
        {SLIDES.map((sl, i) => (
          <div key={i} className={'lx-slide' + (i === idx ? ' is-on' : '')} style={{ background: sl.bg }} aria-hidden={i !== idx}>
            {!hasPhoto(sl.bg) && <div className="lx-slide-glyph">{sl.glyph}</div>}
          </div>
        ))}
        <div className="lx-phone-shade" />
        <div className="lx-phone-rail"><span>♥</span><small>{s.likes}</small></div>
        <div className="lx-phone-caption">
          <p>{s.text}</p>
          <div className="lx-ptag">
            <span className="lx-ptag-thumb" style={s.thumb ? { background: `url(${s.thumb}) center / cover` } : undefined}>{s.thumb ? null : s.glyph}</span>
            <span className="lx-ptag-info"><b>{s.title}</b><small>{s.price}</small></span>
            <span className="lx-ptag-buy">Buy</span>
          </div>
        </div>
      </div>

      {/* Floating notifications · what the creator sees while people shop */}
      <div className="lx-float lx-float-a">
        <span className="lx-float-icon"><ShoppingBag size={14} /></span>
        <span key={idx} className="lx-float-text"><b>New order</b><small>{s.title} · {s.price}</small></span>
      </div>
      <div className="lx-float lx-float-b">
        <span className="lx-float-icon is-violet"><Mail size={14} /></span>
        <span><b>+1 subscriber</b><small>added to your list</small></span>
      </div>
    </div>
  );
}

function Marquee() {
  const row = [...TILES, ...TILES]; // doubled so the loop is seamless
  return (
    <section className="lx-marquee" aria-label="What creators sell on skrl">
      <div className="lx-marquee-label">Sell anything you make</div>
      <div className="lx-marquee-track">
        {row.map(([e, label, c], i) => (
          <div key={i} className="lx-tile" style={{ '--tile': c }} aria-hidden={i >= TILES.length}>
            <span className="lx-tile-e">{e}</span>{label}
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="lx-section">
      <div className="lx-wrap">
        <SectionHead eyebrow="How it works" title={<>From camera roll to checkout <span className="lx-muted-h">in an afternoon.</span></>} />
        <ol className="lx-steps">
          {STEPS.map(({ icon: Icon, title, body, tint }, i) => (
            <li key={title} className="lx-step lx-reveal" style={{ '--tint': tint, transitionDelay: `${i * 90}ms` }}>
              <div className="lx-step-top">
                <span className="lx-step-icon"><Icon size={20} /></span>
                <span className="lx-step-n">0{i + 1}</span>
              </div>
              <h3>{title}</h3>
              <p>{body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Numbers() {
  return (
    <section className="lx-numbers">
      <div className="lx-wrap lx-numbers-grid">
        {NUMBERS.map(([n, label], i) => (
          <div key={label} className="lx-number lx-reveal" style={{ transitionDelay: `${i * 80}ms` }}>
            <b>{n}</b><span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="lx-section" id="features">
      <div className="lx-wrap">
        <SectionHead
          eyebrow="Features"
          title={<>Everything a creator shop needs. <span className="lx-muted-h">Nothing it doesn’t.</span></>}
          sub="Pages, checkout, your customer list, AI video and analytics, all in one studio."
        />
        <div className="lx-bento">
          <article className="lx-card lx-card-wide lx-reveal" style={{ '--tint': '#c4a1ff' }}>
            <div className="lx-card-copy">
              <span className="lx-card-kicker">Pages</span>
              <h3>Pages for every kind of drop</h3>
              <p>A scrolling home feed, single-product pages for ads, collections and multi-step funnels, each at its own link.</p>
            </div>
            <div className="lx-pages">
              {[['🏠', 'Home feed', 'nova.shop'], ['📦', 'Pillow Hoodie', 'nova.shop/p/hoodie'], ['🗂', 'Summer drop', 'nova.shop/c/summer'], ['⚡', 'Creator kit funnel', 'nova.shop/f/kit']].map(([e, t, u]) => (
                <div key={u} className="lx-page-row">
                  <span className="lx-page-ico">{e}</span><span><b>{t}</b><small>{u}</small></span><ArrowRight size={14} />
                </div>
              ))}
            </div>
          </article>

          <article className="lx-card lx-reveal" style={{ '--tint': '#5bd4a9', transitionDelay: '80ms' }}>
            <div className="lx-card-copy">
              <span className="lx-card-kicker">Audience</span>
              <h3>Your list, not ours</h3>
              <p>Every buyer and subscriber is yours to export, any time.</p>
            </div>
            <div className="lx-list">
              {[['cara.lin@gmail.com', '#ffb4c6'], ['j.avery@outlook.com', '#8fd3f4'], ['riverbel@proton.me', '#ffe8a8']].map(([m, c]) => (
                <div key={m} className="lx-list-row"><span className="lx-list-av" style={{ background: c }}>{m[0].toUpperCase()}</span>{m}</div>
              ))}
              <div className="lx-list-export"><Download size={13} /> Export CSV</div>
            </div>
          </article>

          <article className="lx-card lx-reveal" style={{ '--tint': '#ffa86b' }}>
            <div className="lx-card-copy">
              <span className="lx-card-kicker">AI studio</span>
              <h3>Product videos from a photo</h3>
              <p>Turn a product shot and a prompt into a vertical clip.</p>
            </div>
            <div className="lx-prompt">
              <div className="lx-prompt-box">slow pan across the hoodie, warm morning light<span className="lx-caret" /></div>
              <div className="lx-prompt-foot"><span><Sparkles size={13} /> 36 credits</span><span className="lx-prompt-go">Generate</span></div>
            </div>
          </article>

          <article className="lx-card lx-card-wide lx-reveal" style={{ '--tint': '#d4ff47', transitionDelay: '80ms' }}>
            <div className="lx-card-copy">
              <span className="lx-card-kicker">Analytics</span>
              <h3>See what actually sells</h3>
              <p>Views, taps, carts and orders for every video and page, so you know which clip earned the sale.</p>
            </div>
            <div className="lx-chart">
              <div className="lx-chart-head"><BarChart3 size={14} /> Revenue · last 7 days</div>
              <svg viewBox="0 0 300 90" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="lxChartFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
                    <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0 70 C40 62 60 40 100 44 S160 20 200 28 S260 12 300 8 V90 H0 Z" fill="url(#lxChartFill)" />
                <path className="lx-chart-line" d="M0 70 C40 62 60 40 100 44 S160 20 200 28 S260 12 300 8" fill="none" stroke="currentColor" strokeWidth="2.5" pathLength="1" />
              </svg>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function Pricing({ onStartFree }) {
  const [yearly, setYearly] = useState(false);
  return (
    <section className="lx-section" id="pricing">
      <div className="lx-wrap">
        <SectionHead center eyebrow="Pricing" title="Simple plans. No cut of your sales." sub="Every plan starts with a 14-day free trial. Cancel any time." />
        <div className="lx-billing" role="group" aria-label="Billing period">
          <button className={!yearly ? 'is-on' : ''} onClick={() => setYearly(false)}>Monthly</button>
          <button className={yearly ? 'is-on' : ''} onClick={() => setYearly(true)}>Yearly <span>−20%</span></button>
        </div>
        <div className="lx-plans">
          {PLANS.map((p, i) => (
            <div key={p.id} className={'lx-plan lx-reveal' + (p.popular ? ' is-popular theme-dark' : '')} style={{ transitionDelay: `${i * 80}ms` }}>
              {p.popular && <div className="lx-plan-badge">Most popular</div>}
              <h3>{p.name}</h3>
              <p className="lx-plan-blurb">{p.blurb}</p>
              <div className="lx-plan-price"><b>${yearly ? p.yearly : p.monthly}</b><span>/ month</span></div>
              <div className="lx-plan-bill">{yearly ? 'billed yearly' : 'billed monthly'}</div>
              <button className={'lx-btn ' + (p.popular ? 'lx-btn-lime' : 'lx-btn-ghost')} onClick={onStartFree}>Start free trial</button>
              <ul>{p.features.map(f => <li key={f}><Check size={14} /> {f}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="lx-section">
      <div className="lx-wrap lx-faq-grid">
        <SectionHead eyebrow="FAQ" title="Questions, answered." />
        <div className="lx-faq lx-reveal">
          {FAQ.map(([q, a], i) => (
            <div key={q} className={'lx-faq-item' + (open === i ? ' is-open' : '')}>
              <button onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}>
                <span>{q}</span><Plus size={16} />
              </button>
              {open === i && <p>{a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onStartFree, onViewStore }) {
  return (
    <section className="lx-section lx-section-last">
      <div className="lx-wrap">
        <div className="lx-final theme-dark lx-reveal">
          <div className="lx-final-word" aria-hidden="true">skrl.</div>
          <h2 className="lx-h2">Start selling where your audience already is.</h2>
          <p>Set up your store in minutes. Free for 14 days.</p>
          <div className="lx-cta-row">
            <button className="lx-btn lx-btn-lime lx-btn-lg" onClick={onStartFree}>Start free <ArrowRight size={16} /></button>
            <button className="lx-btn lx-btn-ghost lx-btn-lg" onClick={onViewStore}>See a live store</button>
          </div>
        </div>
      </div>
    </section>
  );
}
